import { memo, useEffect, lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { Page } from "./Routes.styles";

import SystemService from "./infrastructure/Services/SystemService";
import ErrorBoundary from "./components/ErrorBoundary";
import RouterErrorFallback from "./components/RouterErrorFallback/RouterErrorFallback";
import AppLayout from "./components/AppLayout/AppLayout";
import RouteLoadingFallback from "./components/RouteLoadingFallback/RouteLoadingFallback";

// React.lazy + dynamic import. Migrated from `@loadable/component` to the
// React 19 native primitive so deslop-js / react-doctor (and any other
// static-analysis tool) can resolve the import graph through these routes.
// Loadable's factory wrapped the dynamic import in a way deslop didn't
// follow, which made the entire transitive subtree show up as unused.
const Login = lazy(() => import("./screens/Login"));
const Signup = lazy(() => import("./screens/Signup"));
const ForgotPassword = lazy(() => import("./screens/ForgotPassword"));
const Install = lazy(() => import("./screens/Install"));
const DashboardWithPermissions = lazy(
  () => import("./components/DashboardWithPermissions"),
);
const Streaming = lazy(() => import("./screens/Streaming"));
const Workspace = lazy(() => import("./screens/Workspace"));
const Welcome = lazy(() => import("./screens/Welcome"));
const SceneDesigns = lazy(() => import("./screens/SceneDesigns/SceneDesigns"));
const Settings = lazy(() => import("./screens/Settings"));
const SimulatedLive = lazy(() => import("./screens/SimulatedLive"));
const Recordings = lazy(() => import("./screens/Recordings"));
const Stats = lazy(() => import("./screens/Stats"));
const Devices = lazy(() => import("./screens/Devices"));
const Recommend2FA = lazy(() => import("./screens/Recommend2FA/Recommend2FA"));

const router = createBrowserRouter([
  {
    errorElement: <RouterErrorFallback />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            path: "/",
            element: <Login />,
          },
          {
            path: "/signup",
            element: <Signup />,
          },
          {
            path: "/forgot-password",
            element: <ForgotPassword />,
          },
          {
            path: "/recommend-2fa",
            element: <Recommend2FA />,
          },
          {
            path: "/install",
            element: <Install />,
          },
          {
            path: "/dashboard",
            element: <DashboardWithPermissions />,
          },
          {
            path: "/live-streams",
            element: <Streaming />,
          },
          {
            path: "/workspace",
            element: <Workspace />,
          },
          {
            path: "/welcome",
            element: <Welcome />,
          },
          {
            path: "/scene-designs",
            element: <SceneDesigns />,
          },
          {
            path: "/settings",
            element: <Settings />,
          },
          {
            path: "/simulated-live",
            element: <SimulatedLive />,
          },
          {
            path: "/team",
            element: <Devices />,
          },
          {
            path: "/recordings",
            element: <Recordings />,
          },
          {
            path: "/stats",
            element: <Stats />,
          },
          {
            path: "/local-recordings",
            element: <Streaming mode="recordings" />,
          },
        ],
      },
    ],
  },
]);

function App() {
  useEffect(() => {
    const initializeSystem = async () => {
      const systemService = new SystemService("api");

      await systemService.keepAlive();
    };

    initializeSystem().catch((error) => {
      console.error("❌ Error initializing system:", error);
    });
  }, []);

  return (
    <ErrorBoundary>
      <Page>
        <Suspense fallback={<RouteLoadingFallback />}>
          <RouterProvider router={router} />
        </Suspense>
      </Page>
    </ErrorBoundary>
  );
}

export default memo(App);
